import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { Mail, Lock } from 'lucide-react';
import Logo from '../Logo';
import AddToHomeScreenModal, { isMobile, STORAGE_KEY } from '../AddToHomeScreenModal';
import '../../styles/auth-gaming.css';
import { useLanguage } from '../../i18n/LanguageContext';
import { useSound } from '../../context/SoundContext';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLanguage();
  const { playSound } = useSound();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verifiedMessage, setVerifiedMessage] = useState(null);
  const [showAddToHomeModal, setShowAddToHomeModal] = useState(false);

  useEffect(() => {
    const verified = searchParams.get('verified');
    if (verified === '1') {
      setVerifiedMessage('success');
    } else if (verified === '0') {
      setVerifiedMessage('invalid');
    }
    if (verified != null) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (isMobile()) {
      try {
        const hidden = localStorage.getItem(STORAGE_KEY) === 'true';
        if (!hidden) {
          setShowAddToHomeModal(true);
        }
      } catch (e) {}
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    playSound('click');
    window.dispatchEvent(new CustomEvent('dios10:unlockAudio'));

    if (!formData.email || !formData.password) {
      setError(t('auth.fillAllFields'));
      playSound('error');
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.login(formData.email, formData.password);
      
      if (response?.success || response?.user) {
        playSound('success');
        // User data already stored by authApi.login
        if (response.token) {
          localStorage.setItem('authToken', response.token);
        }
        window.dispatchEvent(new CustomEvent('dios10:userLoggedIn'));
        // Navigate to home
        navigate('/');
      }
    } catch (err) {
      setError(err.message || t('auth.loginFailed'));
      playSound('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <AddToHomeScreenModal
        show={showAddToHomeModal}
        onClose={() => setShowAddToHomeModal(false)}
      />
      {/* Auth Background */}
      <div
        className="auth-background"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/login.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat'
        }}
      ></div>
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4" style={{ transform: 'scale(2)' }}>
            <Logo size="xl" showText={false} />
          </div>
        </div>

        {/* Login Form */}
        <div className="football-card glass-football auth-card-warm p-8">
          <h2 className="text-2xl font-bold mb-6 text-center">{t('auth.signIn')}</h2>

          {verifiedMessage === 'success' && (
            <div className="bg-green-50 border border-green-200 p-4 mb-6">
              <p className="text-green-800 text-sm">{t('auth.emailVerified')}</p>
            </div>
          )}
          {verifiedMessage === 'invalid' && (
            <div className="bg-red-50 border border-red-200 p-4 mb-6">
              <p className="text-red-800 text-sm">{t('auth.emailVerifiedInvalid')}</p>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 p-4 mb-6">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block mb-2 flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                {t('auth.email')}
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full"
                placeholder="your.email@example.com"
                required
              />
            </div>

            <div>
              <label className="block mb-2 flex items-center">
                <Lock className="w-4 h-4 mr-2" />
                {t('auth.password')}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full"
                placeholder="Enter your password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-football w-full text-lg py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm text-blue-400 hover:text-blue-300">
              {t('auth.forgotPassword')}
            </Link>
          </div>

          <div className="mt-6 text-center">
            <p>
              {t('auth.noAccount')}{' '}
              <Link to="/register" className="font-semibold">
                {t('auth.registerNow')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

